import { apiClientFetch } from "@packages/api-client/client-config";
import {
	getTaskQueryKey,
	getTaskQueryOptions,
} from "@packages/api-client/hooks/planningTasksController";
import {
	LivestockOperationType,
	resolveTaskRecordTarget,
	type TaskRecordTarget,
} from "@packages/shared-types/livestock";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	ArrowLeft,
	Calendar,
	CheckCircle2,
	Clock,
	Flag,
	MapPin,
	User,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCompleteTask } from "@/api/planning/tasks";
import { OfflineUnavailable } from "@/components/OfflineUnavailable";
import { CommentsThread } from "@/components/planning/CommentsThread";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useFarmScope } from "@/hooks/useFarmScope";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { findOfflineTaskRow } from "@/hooks/useTasksListData";
import { isOfflineQueued } from "@/lib/offline/defineOfflineMutation";
import { loadUnlessOffline } from "@/lib/offline/loadUnlessOffline";
import { cn } from "@/utils/utils";
import { MeasurementFormDialog } from "../../../livestock/$establishmentId/measurements/-components/MeasurementFormDialog";
import { CalvingFormDialog } from "../../../livestock/$establishmentId/operations/-components/CalvingFormDialog";
import { OperationFormDialog } from "../../../livestock/$establishmentId/operations/-components/OperationFormDialog";
import { TaskDetailOffline } from "./-components/TaskDetailOffline";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/planning/tasks/$taskId/",
)({
	staticData: { offline: "ready" },
	loader: ({ context, params }) =>
		loadUnlessOffline(async () => {
			await context.queryClient.ensureQueryData(
				getTaskQueryOptions(params.orgId, params.taskId, {
					client: apiClientFetch,
				}),
			);
		}),
	component: TaskDetailPage,
});

const STATUS_COLORS: Record<string, string> = {
	PENDING: "bg-base-300 text-base-content",
	APPROVED: "bg-primary text-primary-content",
	SCHEDULED: "bg-info text-info-content",
	IN_PROGRESS: "bg-warning text-warning-content",
	COMPLETED: "bg-success text-success-content",
	CANCELLED: "bg-error text-error-content",
};

const PRIORITY_COLORS: Record<number, string> = {
	1: "text-error",
	2: "text-warning",
	3: "text-info",
	4: "text-base-content/70",
};

const PRIORITY_LABELS: Record<number, string> = {
	1: "critical",
	2: "high",
	3: "medium",
	4: "low",
};

function TaskDetailPage() {
	const { orgId, taskId } = Route.useParams();
	const online = useOnlineStatus();
	const queryClient = useQueryClient();
	const scope = useFarmScope();

	if (!online) {
		const cachedFull = queryClient.getQueryData(getTaskQueryKey(orgId, taskId));
		if (!cachedFull) {
			const row = findOfflineTaskRow(
				queryClient,
				orgId,
				scope.kind === "single" ? scope.establishmentId : undefined,
				taskId,
			);
			// Prepared dataset row → offline view with the queueable complete
			// action; nothing at all → the "prepare in the Offline Center" panel.
			return row ? (
				<TaskDetailOffline row={row} />
			) : (
				<OfflineUnavailable variant="preparable" />
			);
		}
	}

	return <TaskDetailFull />;
}

function TaskDetailFull() {
	const { orgId, taskId } = Route.useParams();
	const { t } = useTranslation();

	const { data: task } = useSuspenseQuery(
		getTaskQueryOptions(orgId, taskId, { client: apiClientFetch }),
	);

	// Completing queues offline (tasks:complete) — a user who loaded this
	// screen online and then lost signal can still mark the task done.
	const updateStatus = useCompleteTask();

	const isCompleted = task.status === "COMPLETED";
	const isCancelled = task.status === "CANCELLED";
	// Offline the completion resolves with the OFFLINE_QUEUED sentinel: onSuccess
	// runs but the invalidation can't refetch, so `task.status` never flips to
	// COMPLETED. Derive the queued state from the mutation result so the button
	// re-locks after enqueue instead of inviting a second tap (each re-tap would
	// enqueue another `tasks:complete`). Online this stays false — the refetch
	// flips `isCompleted` and hides the button as before.
	const queued = isOfflineQueued(updateStatus.data);

	// Task→record link v1 (plan item 13): completing a cattle task whose
	// type/title maps to a livestock record offers an opt-in prompt to create
	// that record prefilled from task context. One-way; provenance rides in
	// `metadata.sourceTaskId` (operations) / a `[task:<id>]` notes suffix
	// (measurements).
	const [recordTarget, setRecordTarget] = useState<TaskRecordTarget | null>(
		null,
	);
	const domainData = (task.domainData ?? {}) as {
		animalId?: unknown;
		herdId?: unknown;
	};
	const taskAnimalId =
		typeof domainData.animalId === "string" ? domainData.animalId : undefined;
	const taskHerdId =
		typeof domainData.herdId === "string" ? domainData.herdId : undefined;

	const maybePromptRecord = () => {
		if (task.domain !== "cattle") return;
		const target = resolveTaskRecordTarget(task);
		if (!target) return;
		const typeLabel =
			target.kind === "operation"
				? t(`livestock.operations.operationTypes.${target.type}`)
				: t(`livestock.measurements.types.${target.type}`);
		toast(t("planning.tasks.recordPrompt", { type: typeLabel }), {
			duration: 10000,
			action: {
				label: t("planning.tasks.recordPromptAction"),
				onClick: () => setRecordTarget(target),
			},
		});
	};

	const handleComplete = () => {
		if (isCompleted || isCancelled || queued || updateStatus.isPending) return;
		updateStatus.mutate(
			{
				organizationId: orgId,
				taskId,
				data: { status: "COMPLETED" },
			},
			{ onSuccess: () => maybePromptRecord() },
		);
	};

	const closeRecordForm = (open: boolean) => {
		if (!open) setRecordTarget(null);
	};
	const isCalvingTarget =
		recordTarget?.kind === "operation" &&
		recordTarget.type === LivestockOperationType.CALVING;

	const formatDate = (dateString: string) =>
		new Date(dateString).toLocaleDateString("pt-BR", {
			day: "2-digit",
			month: "long",
			year: "numeric",
		});

	const assignedName = task.assignedTo
		? [task.assignedTo.firstName, task.assignedTo.lastName]
				.filter(Boolean)
				.join(" ") || task.assignedTo.email
		: null;

	const completedByName = task.completedBy
		? [task.completedBy.firstName, task.completedBy.lastName]
				.filter(Boolean)
				.join(" ") || task.completedBy.email
		: null;

	const createdByName = task.createdBy
		? [task.createdBy.firstName, task.createdBy.lastName]
				.filter(Boolean)
				.join(" ") || task.createdBy.email
		: null;

	const approvedByName = task.approvedBy
		? [task.approvedBy.firstName, task.approvedBy.lastName]
				.filter(Boolean)
				.join(" ") || task.approvedBy.email
		: null;

	return (
		<>
			{/* Scrollable content — extra bottom padding on mobile for the sticky CTA */}
			<div className="flex flex-col gap-4 pb-4 md:pb-0 mb-16 md:mb-0">
				{/* Back navigation */}
				<div className="flex items-center gap-2">
					<Link
						to="/app/org/$orgId/planning/tasks"
						params={{ orgId }}
						className="btn btn-ghost btn-sm gap-1 -ml-2"
					>
						<ArrowLeft className="h-4 w-4" />
						{t("common.back")}
					</Link>
				</div>

				{/* Title + status */}
				<div className="flex flex-col gap-2">
					<div className="flex items-start justify-between gap-3">
						<h1 className="text-xl font-bold text-base-content leading-snug flex-1">
							{task.title}
						</h1>
						<Badge className={cn("shrink-0", STATUS_COLORS[task.status])}>
							{t(`planning.tasks.status.${task.status.toLowerCase()}`)}
						</Badge>
					</div>
					{task.description && (
						<p className="text-base-content/70 text-sm leading-relaxed">
							{task.description}
						</p>
					)}
				</div>

				{/* Meta cards */}
				<div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
					<MetaChip
						icon={<Calendar className="h-4 w-4" />}
						label={t("planning.tasks.detail.scheduledDate")}
						value={formatDate(task.scheduledDate)}
					/>
					{task.dueDate && (
						<MetaChip
							icon={<Calendar className="h-4 w-4 text-warning" />}
							label={t("planning.tasks.detail.dueDate")}
							value={formatDate(task.dueDate)}
						/>
					)}
					<MetaChip
						icon={
							<Flag className={cn("h-4 w-4", PRIORITY_COLORS[task.priority])} />
						}
						label={t("planning.tasks.detail.priority")}
						value={t(
							`planning.tasks.priority.${PRIORITY_LABELS[task.priority] ?? "medium"}`,
						)}
					/>
					{task.estimatedDurationMin && (
						<MetaChip
							icon={<Clock className="h-4 w-4" />}
							label={t("planning.tasks.detail.duration")}
							value={`${task.estimatedDurationMin} min`}
						/>
					)}
					{task.establishment && (
						<MetaChip
							icon={<MapPin className="h-4 w-4" />}
							label={t("planning.tasks.detail.establishment")}
							value={task.establishment.name}
						/>
					)}
					{assignedName && (
						<MetaChip
							icon={<User className="h-4 w-4" />}
							label={t("planning.tasks.detail.assignedTo")}
							value={assignedName}
						/>
					)}
					{createdByName && (
						<MetaChip
							icon={<User className="h-4 w-4" />}
							label={t("planning.tasks.detail.createdBy")}
							value={createdByName}
						/>
					)}
					{approvedByName && (
						<MetaChip
							icon={<CheckCircle2 className="h-4 w-4" />}
							label={t("planning.tasks.detail.approvedBy")}
							value={approvedByName}
						/>
					)}
				</div>

				{/* Domain / objective */}
				<Card>
					<CardContent className="pt-4 flex flex-col gap-2">
						<div className="flex items-center justify-between">
							<span className="text-xs font-medium text-base-content/60 uppercase tracking-wide">
								{t("planning.tasks.detail.domain")}
							</span>
							<Badge variant="outline" className="capitalize">
								{task.domain}
							</Badge>
						</div>
						{(task.subObjective || task.objective) && (
							<div className="flex items-start justify-between gap-2">
								<span className="text-xs font-medium text-base-content/60 uppercase tracking-wide">
									{t("planning.tasks.detail.objective")}
								</span>
								<span className="text-sm text-right text-base-content/80">
									{task.subObjective?.name ?? task.objective?.name}
								</span>
							</div>
						)}
					</CardContent>
				</Card>

				{/* Completion info */}
				{isCompleted && (
					<Card className="border-success/30 bg-success/5">
						<CardContent className="pt-4 flex flex-col gap-1">
							<div className="flex items-center gap-2 text-success">
								<CheckCircle2 className="h-4 w-4" />
								<span className="text-sm font-medium">
									{t("planning.tasks.detail.completedAt", {
										date: task.completedAt ? formatDate(task.completedAt) : "-",
									})}
								</span>
							</div>
							{completedByName && (
								<span className="text-xs text-base-content/60 ml-6">
									{t("planning.tasks.detail.completedBy", {
										name: completedByName,
									})}
								</span>
							)}
							{task.executionNotes && (
								<p className="text-sm text-base-content/70 mt-2 ml-6">
									{task.executionNotes}
								</p>
							)}
						</CardContent>
					</Card>
				)}

				{/* Completion queued offline — the write is persisted and will replay
				    on reconnect; lock the CTA so it isn't enqueued twice. */}
				{queued && (
					<div className="flex items-center gap-2 rounded-xl border border-success/30 bg-success/5 px-4 py-3 text-sm text-success">
						<CheckCircle2 className="h-4 w-4 shrink-0" />
						<span>{t("planning.tasks.completeQueued")}</span>
					</div>
				)}

				{/* Comments */}
				<Card>
					<CardContent className="pt-4">
						<CommentsThread
							organizationId={orgId}
							parentType="TASK"
							parentId={taskId}
						/>
					</CardContent>
				</Card>

				{/* Desktop Complete button */}
				{!isCompleted && !isCancelled && !queued && (
					<div className="hidden md:flex justify-end">
						<Button
							onClick={handleComplete}
							disabled={updateStatus.isPending}
							className="gap-2"
						>
							{updateStatus.isPending && (
								<span className="loading loading-spinner loading-xs" />
							)}
							<CheckCircle2 className="h-4 w-4" />
							{t("planning.tasks.markComplete")}
						</Button>
					</div>
				)}
			</div>

			{/* Mobile sticky CTA — pinned above the bottom tab bar */}
			{!isCompleted && !isCancelled && !queued && (
				<div
					className="md:hidden fixed left-0 right-0 z-30 bg-base-100/95 backdrop-blur-sm border-t border-base-300 px-4 py-3"
					style={{
						bottom: "calc(84px + env(safe-area-inset-bottom))",
					}}
				>
					<Button
						className="w-full gap-2 h-12 text-base"
						onClick={handleComplete}
						disabled={updateStatus.isPending}
					>
						{updateStatus.isPending ? (
							<span className="loading loading-spinner loading-sm" />
						) : (
							<CheckCircle2 className="h-5 w-5" />
						)}
						{t("planning.tasks.markComplete")}
					</Button>
				</div>
			)}

			{/* Task→record link: the prompt's action opens the mapped record form
			    prefilled from task context (farm, herd/animal when derivable). */}
			<OperationFormDialog
				orgId={orgId}
				establishmentId={task.establishmentId}
				lockedAnimalId={taskAnimalId}
				lockedHerdId={taskAnimalId ? undefined : taskHerdId}
				initialType={
					recordTarget?.kind === "operation" && !isCalvingTarget
						? recordTarget.type
						: undefined
				}
				initialDescription={task.title}
				sourceTaskId={task.id}
				open={recordTarget?.kind === "operation" && !isCalvingTarget}
				onOpenChange={closeRecordForm}
			/>
			<MeasurementFormDialog
				orgId={orgId}
				establishmentId={task.establishmentId}
				lockedAnimalId={taskAnimalId}
				lockedHerdId={taskAnimalId ? undefined : taskHerdId}
				initialType={
					recordTarget?.kind === "measurement" ? recordTarget.type : undefined
				}
				sourceTaskId={task.id}
				open={recordTarget?.kind === "measurement"}
				onOpenChange={closeRecordForm}
			/>
			<CalvingFormDialog
				orgId={orgId}
				establishmentId={task.establishmentId}
				lockedDamAnimalId={taskAnimalId}
				open={isCalvingTarget}
				onOpenChange={closeRecordForm}
			/>
		</>
	);
}

function MetaChip({
	icon,
	label,
	value,
}: {
	icon: React.ReactNode;
	label: string;
	value: string;
}) {
	return (
		<div className="bg-base-100 rounded-xl p-3 border border-base-200 flex flex-col gap-1">
			<div className="flex items-center gap-1.5 text-base-content/60">
				{icon}
				<span className="text-[10px] uppercase tracking-wide font-medium">
					{label}
				</span>
			</div>
			<span className="text-sm font-medium text-base-content leading-snug">
				{value}
			</span>
		</div>
	);
}
